import type { Action } from '@0xtorch/core'
import {
  ApiSecretRequiredException,
  AssetNotFoundException,
} from '../../errors'
import type { FetchApiData } from '../../types'
import { createGetAsset } from '../../utils/createGetAsset'
import { binanceAccountCategory } from '../constants'
import { createClient } from '../createClient'
import { responseSchema } from './schema'

export const fetchApiData: FetchApiData = async ({
  apiKey,
  apiSecret,
  assets,
  config,
  from,
  createHeaders,
  proxy,
  symbolAssetMap,
  to,
}) => {
  // Pre-condition checks
  if (!apiSecret) {
    throw new ApiSecretRequiredException()
  }
  // Sub type is not required for convert history (existSubType: false)

  const getAsset = createGetAsset({ assets, symbolAssetMap })

  const client = createClient({
    apiKey,
    apiSecret,
    createHeaders,
    proxy,
  })

  // API request
  const response = await client.run({
    method: 'GET',
    path: '/sapi/v1/convert/tradeFlow',
    query: {
      startTime: (from ?? 0).toString(),
      endTime: (to ?? Date.now()).toString(),
      limit: config.limit.toString(),
    },
    responseSchema,
  })

  // Sort list by createTime in ascending order
  const sortedItems = response.list.sort((a, b) => a.createTime - b.createTime)

  let fetchedFrom: number | undefined
  let fetchedTo: number | undefined
  const actions: Action[] = []

  for (const item of sortedItems) {
    // Update fetched range
    fetchedFrom =
      fetchedFrom === undefined
        ? item.createTime
        : Math.min(fetchedFrom, item.createTime)
    fetchedTo =
      fetchedTo === undefined
        ? item.createTime
        : Math.max(fetchedTo, item.createTime)

    // Only SUCCESS status is supported
    if (item.orderStatus !== 'SUCCESS') {
      throw new Error(`Unsupported orderStatus: ${item.orderStatus}`)
    }

    const toAsset = getAsset(item.toAsset)
    if (!toAsset) {
      throw new AssetNotFoundException(item.toAsset)
    }
    const fromAsset = getAsset(item.fromAsset)
    if (!fromAsset) {
      throw new AssetNotFoundException(item.fromAsset)
    }

    actions.push({
      evidence: 'system-rule',
      order: 0,
      source: `${binanceAccountCategory}_${config.type}_${item.orderId}`,
      type: 'trade',
      timestamp: item.createTime,
      transfers: [
        {
          asset: toAsset,
          amount: item.toAmount,
          direction: 'in',
        },
        {
          asset: fromAsset,
          amount: item.fromAmount,
          direction: 'out',
        },
      ],
    })
  }

  // Sort actions by timestamp (asc) per spec
  const sortedActions = actions.sort((a, b) => a.timestamp - b.timestamp)

  return {
    actions: sortedActions,
    count: response.list.length,
    from: fetchedFrom,
    sources: [],
    stopped: false,
    to: fetchedTo,
  }
}
